import React from "react";
import PropTypes from "prop-types";

export default class Typeahead extends React.PureComponent {
	static filterFuncDefaultProp = (options, searchQuery) => {
		if (!options || (typeof searchQuery  !== 'string')) {
			return [];
		}

		const result = options.filter(
			(o) =>
				o &&
				o.displayValue &&
				o.displayValue.trim().toLowerCase().indexOf(searchQuery.trim().toLowerCase()) > -1
		);

		return result;
	}

	static propTypes = {
		options: PropTypes.arrayOf(
			PropTypes.shape({
				key: PropTypes.string,
				displayValue: PropTypes.string
			})
		),
		filterFunc: PropTypes.func,
		value: PropTypes.string,
		inputId: PropTypes.string,
		inputProps: PropTypes.object,
		onInputChange: PropTypes.func,
		onInputBlur: PropTypes.func,
		onMatchSelect: PropTypes.func,
		loadingCondition: PropTypes.func,
		loadingMessage: PropTypes.string,
		maxMatchesCount: PropTypes.number,
		typeaheadClassName: PropTypes.string,
		matchesClassName: PropTypes.string,
		matchLiClassName: PropTypes.string,
		matchUlClassName: PropTypes.string,
		matchClassName: PropTypes.string,
		loadingClassName: PropTypes.string
	};

	static defaultProps = {
		options: [],
		filterFunc: this.filterFuncDefaultProp,
		value: '',
		inputId: null,
		inputProps: {},
		loadingCondition: options => !options,
		loadingMessage: 'Loading...',
		maxMatchesCount: 25,
		typeaheadClassName: 'typeahead',
		matchesClassName: 'typeahead-matches',
		matchLiClassName: 'typeahead-li',
		matchUlClassName: 'typeahead-ul',
		matchClassName: 'typeahead-match',
		loadingClassName: 'typeahead-loading'
	};

	constructor(props) {
		super(props);

		this.state = {
			matches: [],
			matchesFocused: false
		};

		this.tabPressed = false;
	}

	componentDidUpdate(prevProps) {
		const { options, value } = this.props;

		if (JSON.stringify(options) !== JSON.stringify(prevProps.options)) {
			this.updateMatches(value);
		}
	}

	updateMatches = searchQuery => {
		const { options, filterFunc, maxMatchesCount } = this.props;
		const matches = filterFunc(options, searchQuery || '');
		const shouldCutMatches = maxMatchesCount && Number.isInteger(maxMatchesCount) && matches.length > maxMatchesCount;

		if (shouldCutMatches) {
			matches.length = maxMatchesCount;
		}

		this.setState({ matches });
	};

	handlePropsInputChange = value => {
		const { onInputChange } = this.props;

		if (onInputChange) {
			onInputChange(value);
		}
	};

	handleInputChange = event => {
		const { value } = event.target;

		this.updateMatches(value);
		this.handlePropsInputChange(value);
	};

	handleInputKeyDown = event => {
		const { matches } = this.state;
		const shouldUpdateValue = event.keyCode === 13 && matches && matches.length === 1;
		this.tabPressed = event.keyCode === 9;

		if (shouldUpdateValue) {
			this.setState({
				matches: [],
				matchesFocused: false
			});

			this.handlePropsInputChange(matches[0].displayValue);
		}
	};

	handleInputFocus = () => {
		this.updateMatches();
		this.handlePropsInputChange('');
	};

	handleInputBlur = () => {
		const { onInputBlur } = this.props;
		let { matchesFocused } = this.state;

		if (this.tabPressed) {
			matchesFocused = matchesFocused || this.tabPressed;
			this.tabPressed = false;
		}

		if (!matchesFocused) {
			this.setState({
				matches: []
			});
		}

		if (onInputBlur) {
			onInputBlur();
		}
	};

	handleMatchOnMouseOver = () => {
		this.setState({ matchesFocused: true });
	};

	handleMatchOnFocus = () => {
		this.handleMatchOnMouseOver();
	};

	handleMatchOnMouseOut = () => {
		this.setState({ matchesFocused: false });
	};

	handleMatchOnBlur = match => {
		const { matches } = this.state;

		if (this.tabPressed) {
			this.tabPressed = false;

			if (matches.indexOf(match) === matches.length - 1) {
				this.setState({
					matches: [],
					matchesFocused: false
				});
			}
		} else {
			this.setState({
				matches: [],
				matchesFocused: false
			});
		}
	};

	handleMatchClick = match => {
		const { onMatchSelect } = this.props;

		this.setState({
			matches: [],
			matchesFocused: false
		});

		this.handlePropsInputChange(match.displayValue);

		if (onMatchSelect) {
			onMatchSelect(match);
		}
	};

	handleMatchKeyDown = (event, match) => {
		this.tabPressed = event.keyCode === 9;

		if (event.keyCode === 13) {
			const { onMatchSelect } = this.props;

			this.setState({
				matches: [],
				matchesFocused: false
			});

			this.handlePropsInputChange(match.displayValue);

			if (onMatchSelect) {
				onMatchSelect(match);
			}
		}
	};

	getInput = () => {
		const { inputId, inputProps, value } = this.props;

		return (
			<input
				value={value}
				onChange={this.handleInputChange}
				onKeyDown={this.handleInputKeyDown}
				onFocus={this.handleInputFocus}
				onBlur={this.handleInputBlur}
				id={inputId}
				{...inputProps}
			/>
		);
	};

	getMatchElement = match => {
		const { matchLiClassName, matchClassName } = this.props;

		return (
			<li key={match.key} className={matchLiClassName}>
				<div
					className={matchClassName}
					onClick={() => this.handleMatchClick(match)}
					role="button"
					tabIndex={0}
					onKeyDown={event => this.handleMatchKeyDown(event, match)}
					onMouseOver={this.handleMatchOnMouseOver}
					onFocus={this.handleMatchOnFocus}
					onMouseOut={this.handleMatchOnMouseOut}
					onBlur={() => this.handleMatchOnBlur(match)}
				>
					{match.displayValue}
				</div>
			</li>
		);
	};

	getMatchesElement = () => {
		const { matches } = this.state;
		const { matchesClassName, matchUlClassName } = this.props;

		if (matches.length === 0) {
			return null;
		}

		const matchElements = matches.map(match => this.getMatchElement(match));

		return (
			<div className={matchesClassName}>
				<ul className={matchUlClassName}>{matchElements}</ul>
			</div>
		);
	};

	getLoadingElement = () => {
		const { loadingMessage, matchesClassName, loadingClassName, value } = this.props;

		return value
			? (
				<div className={matchesClassName}>
					<div className={loadingClassName}>{loadingMessage}</div>
				</div>
			)
			: null;
	};

	render() {
		const { options, loadingCondition, typeaheadClassName } = this.props;

		return (
			<div className={typeaheadClassName}>
				{this.getInput()}
				{loadingCondition && loadingCondition(options) ? this.getLoadingElement() : this.getMatchesElement()}
			</div>
		);
	}
}
